import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Theme,
  alpha,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../views/components/common/SkuCopyRow";
import { InventoryMovementRow } from "../../../../../../models/inventory/InventoryAdmin";
import {
  formatDateWithoutTime,
  formatTimeOnly,
  parseUtcDate,
} from "../../../../../../views/components/common/functions/formatDate";

interface AuditSkuDrawerProps {
  open: boolean;
  clientSku: string | null;
  productName?: string | null;
  rows: InventoryMovementRow[];
  isLoading: boolean;
  onClose: () => void;
}

type SortDirection = "asc" | "desc";

/** "PACK_BUILD" / "pack-build" → "Pack Build" */
const humanize = (value: string | null | undefined) =>
  (value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || "—";

const getTime = (value: string) => parseUtcDate(value)?.getTime() ?? 0;

type ChipTone = "success" | "error" | "warning" | "info" | "primary" | "secondary" | "default";

/**
 * Keyword rules, checked in order (first match wins). Log types come from the
 * backend as free-form codes, so match on words rather than exact values.
 */
const LOG_TYPE_TONES: { tone: ChipTone; keywords: string[] }[] = [
  // Release before reserve so "UNRESERVE" / "RESERVE_RELEASE" don't match reserve.
  { tone: "secondary", keywords: ["release", "unreserve", "unreserved", "cancel", "cancelled", "void"] },
  { tone: "info", keywords: ["reserve", "reserved", "reservation", "allocate", "allocated", "hold"] },
  { tone: "warning", keywords: ["adjust", "adjustment", "correction", "audit", "recount", "count", "manual"] },
  {
    tone: "error",
    keywords: ["out", "outward", "dispatch", "dispatched", "ship", "shipped", "consume", "consumed",
      "deduct", "remove", "removed", "damage", "damaged", "loss", "lost", "write", "scrap", "redeem", "redemption"],
  },
  {
    tone: "success",
    keywords: ["in", "inward", "receive", "received", "grn", "restock", "return", "returned", "add", "added", "opening"],
  },
  { tone: "primary", keywords: ["pack", "build", "built", "assemble", "assembly", "kit", "kitting"] },
];

const getLogTypeTone = (row: InventoryMovementRow): ChipTone => {
  const words = (row.logType || "").toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  const match = LOG_TYPE_TONES.find(({ keywords }) => words.some((w) => keywords.includes(w)));
  if (match) return match.tone;
  // Unknown type: fall back to the direction of the stock movement.
  if (row.onHandDelta > 0) return "success";
  if (row.onHandDelta < 0) return "error";
  if (row.reservedDelta !== 0) return "info";
  return "default";
};

const logTypeChipSx = (tone: ChipTone) => ({
  fontWeight: 600,
  ...(tone === "default"
    ? {}
    : {
        color: `${tone}.dark`,
        bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.12),
        border: (theme: Theme) => `1px solid ${alpha(theme.palette[tone].main, 0.3)}`,
      }),
});

function DeltaValue({ value, bold }: { value: number; bold?: boolean }) {
  if (!value) {
    return (
      <CustomTypography variant="body2" color="text.disabled">
        0
      </CustomTypography>
    );
  }
  return (
    <CustomTypography
      variant="body2"
      fontWeight={bold ? 700 : 600}
      color={value < 0 ? "error.main" : "success.main"}
    >
      {value > 0 ? "+" : ""}
      {value}
    </CustomTypography>
  );
}

function SummaryStat({
  label,
  value,
  signed,
}: {
  label: string;
  value: number;
  signed?: boolean;
}) {
  const color = !signed || value === 0 ? "text.primary" : value < 0 ? "error.main" : "success.main";
  return (
    <Paper
      elevation={0}
      sx={{
        flex: 1,
        minWidth: 0,
        p: { xs: 1.25, sm: 1.75 },
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
      }}
    >
      <CustomTypography variant="body3" color="text.secondary" sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </CustomTypography>
      <CustomTypography variant="heading4" fontWeight={700} color={color}>
        {signed && value > 0 ? "+" : ""}
        {value}
      </CustomTypography>
    </Paper>
  );
}

export default function AuditSkuDrawer({
  open,
  clientSku,
  productName,
  rows,
  isLoading,
  onClose,
}: AuditSkuDrawerProps) {
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [typeFilter, setTypeFilter] = useState<string | null>(null);

  // Reset local view state whenever a different SKU is opened.
  useEffect(() => {
    setTypeFilter(null);
    setSortDirection("desc");
  }, [clientSku]);

  const logTypes = useMemo(() => {
    const counts = new Map<string, number>();
    rows.forEach((row) => counts.set(row.logType, (counts.get(row.logType) || 0) + 1));
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const visibleRows = useMemo(() => {
    const filtered = typeFilter ? rows.filter((row) => row.logType === typeFilter) : rows;
    return [...filtered].sort((a, b) => {
      const diff = getTime(a.createdAt) - getTime(b.createdAt) || a.id - b.id;
      return sortDirection === "asc" ? diff : -diff;
    });
  }, [rows, typeFilter, sortDirection]);

  const totals = useMemo(
    () =>
      visibleRows.reduce(
        (acc, row) => ({
          onHand: acc.onHand + row.onHandDelta,
          reserved: acc.reserved + row.reservedDelta,
        }),
        { onHand: 0, reserved: 0 },
      ),
    [visibleRows],
  );

  const headCellSx = {
    fontWeight: 600,
    fontSize: 12,
    color: "text.secondary",
    textTransform: "uppercase" as const,
    letterSpacing: 0.4,
    whiteSpace: "nowrap" as const,
    backgroundColor: "grey.50",
    py: 1.25,
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 200,
        "& .MuiDrawer-paper": {
          zIndex: (theme) => theme.zIndex.drawer + 200,
        },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 720, md: 920 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
          backgroundColor: "background.paper",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: { xs: 2, sm: 2.5 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
            flexShrink: 0,
          }}
        >
          <Box minWidth={0}>
            <CustomTypography variant="heading4" fontWeight={700}>
              SKU Audit Ledger
            </CustomTypography>
            <CustomTypography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {productName || "—"}
            </CustomTypography>
            <Box sx={{ mt: 0.75 }}>
              <SkuCopyRow sku={clientSku} fontWeight={600} />
            </Box>
          </Box>
          <IconButton onClick={onClose} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />

        {/* Summary */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            pt: 2,
            display: "flex",
            gap: { xs: 1, sm: 1.5 },
            flexShrink: 0,
          }}
        >
          <SummaryStat label="Entries" value={visibleRows.length} />
          <SummaryStat label="Net On-Hand Change" value={totals.onHand} signed />
          <SummaryStat label="Net Reserved Change" value={totals.reserved} signed />
        </Box>

        {/* Type filter */}
        {logTypes.length > 1 && (
          <Box
            sx={{
              px: { xs: 2, sm: 3 },
              pt: 1.5,
              display: "flex",
              gap: 1,
              flexWrap: "wrap",
              flexShrink: 0,
            }}
          >
            <Chip
              label={`All (${rows.length})`}
              size="small"
              color={typeFilter === null ? "primary" : "default"}
              variant={typeFilter === null ? "filled" : "outlined"}
              onClick={() => setTypeFilter(null)}
            />
            {logTypes.map(([type, count]) => (
              <Chip
                key={type}
                label={`${humanize(type)} (${count})`}
                size="small"
                color={typeFilter === type ? "primary" : "default"}
                variant={typeFilter === type ? "filled" : "outlined"}
                onClick={() => setTypeFilter(typeFilter === type ? null : type)}
              />
            ))}
          </Box>
        )}

        {/* Table */}
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 2, flex: 1, minHeight: 0, display: "flex" }}>
          {isLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                flex: 1,
                alignSelf: "flex-start",
                p: 3,
                borderRadius: 2,
                textAlign: "center",
                border: (theme) => `1px dashed ${theme.palette.divider}`,
              }}
            >
              <CustomTypography variant="body2" color="text.secondary">
                No movement entries found for this SKU.
              </CustomTypography>
            </Paper>
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                maxHeight: "100%",
                borderRadius: 2,
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx} sortDirection={sortDirection}>
                      <TableSortLabel
                        active
                        direction={sortDirection}
                        onClick={() =>
                          setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
                        }
                      >
                        Date &amp; Time
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={headCellSx}>Type</TableCell>
                    <TableCell sx={headCellSx}>Reference</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      On-Hand Δ
                    </TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Reserved Δ
                    </TableCell>
                    <TableCell sx={headCellSx}>Pack</TableCell>
                    <TableCell sx={headCellSx}>Admin</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Ledger #
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleRows.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "& td": { py: 1.25, verticalAlign: "middle" },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        <CustomTypography variant="body2" fontWeight={500}>
                          {formatDateWithoutTime(row.createdAt, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })}
                        </CustomTypography>
                        <CustomTypography variant="body3" color="text.secondary">
                          {formatTimeOnly(row.createdAt)}
                        </CustomTypography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={humanize(row.logType)}
                          size="small"
                          sx={logTypeChipSx(getLogTypeTone(row))}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {row.refType ? (
                          <CustomTypography variant="body2">
                            {humanize(row.refType)}
                            {row.refId ? (
                              <Box component="span" sx={{ color: "text.secondary", ml: 0.5 }}>
                                #{row.refId}
                              </Box>
                            ) : null}
                          </CustomTypography>
                        ) : (
                          <CustomTypography variant="body2" color="text.disabled">
                            —
                          </CustomTypography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <DeltaValue value={row.onHandDelta} bold />
                      </TableCell>
                      <TableCell align="right">
                        <DeltaValue value={row.reservedDelta} />
                      </TableCell>
                      <TableCell>
                        <CustomTypography
                          variant="body2"
                          color={row.packId ? "text.primary" : "text.disabled"}
                        >
                          {row.packId ? `#${row.packId}` : "—"}
                        </CustomTypography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 160 }} title={row.adminName || undefined}>
                        <CustomTypography
                          variant="body2"
                          sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          color={row.adminName || row.adminId ? "text.primary" : "text.disabled"}
                        >
                          {row.adminName || (row.adminId ? `#${row.adminId}` : "—")}
                        </CustomTypography>
                      </TableCell>
                      <TableCell align="right">
                        <CustomTypography variant="body3" color="text.secondary">
                          #{row.id}
                        </CustomTypography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
